import React, { useCallback, useEffect, useMemo, useRef, memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { AutoComplete, DatePicker, Input, Select, Tooltip } from 'antd';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

import { stepperInfo } from '../../forms/CreateRide/stepperInfo';
import { useRidersAutoComplete } from '../../../hooks/useRidersAutoComplete';
import { useCheckRiderForMatching, getUniqueUserFieldValues } from '../../../hooks/useCheckRiderForMatching';
import { RiderHelper } from '../../../utils/RiderHelper';
import { LodashHelper } from '../../../utils';
import {
  COVID_ERROR_MESSAGE,
  COVID_OPTIONS,
  COMMUNICATION_TYPES,
  languages,
} from '../../../constants/constants';
import { communicationsOptions } from '../../forms/mock';
import { clearRidersInfo, setRiderInfo } from '../../../store/riders';
import { MatchedRidersSnackBar } from '../../forms/CreateRider/MatchedRidersSnackBar';
import { MatchedExactRiderSnackBar } from '../../forms/CreateRider/MatchedExactRiderSnackBar';
import infoIcon from '../../../assets/svgs/infoIcon.svg';
import styles from './quickScheduleNew.module.css';

dayjs.extend(customParseFormat);

const DOB_STORAGE_FORMAT = 'YYYY/MM/DD';

function parseDobValue(dob) {
  if (!dob) return null;
  const strict = dayjs(dob, [DOB_STORAGE_FORMAT, 'DD/MM/YYYY', 'MM/DD/YYYY', 'YYYY-MM-DD'], true);
  if (strict.isValid()) return strict;
  const loose = dayjs(dob);
  return loose.isValid() ? loose : null;
}

function mergeDraftStepOneForForm(step1) {
  if (!step1) return step1;
  const defaults = stepperInfo[0].values;
  const riderInfo = {
    ...defaults.riderInfo,
    ...(step1.riderInfo || {}),
    ...(step1.riderDraft || {}),
  };
  return { ...defaults, ...step1, riderInfo };
}

const FieldLabel = ({ children, required }) => (
  <label className={`${styles.fieldLabel} ${required ? styles.required : ''}`}>
    {children}
  </label>
);

const fieldError = (errors, path) => {
  const parts = path.split('.');
  let err = errors;
  for (const p of parts) {
    err = err?.[p];
  }
  if (!err || err === '*required') return null;
  return typeof err === 'string' ? err : null;
};

const formatPhoneDisplay = (digits) => {
  const d = String(digits || '').replace(/\D/g, '').slice(0, 10);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
};

const RiderDetailsPanel = ({
  showCovidField,
  showClinicSelect,
  healthOrganizations = [],
  onOrganizationChange,
  draft,
  onValuesChange,
  onValidChange,
  onMatchedRidersChange,
  handleSubmit,
}) => {
  const dispatch = useDispatch();
  const riders = useSelector((state) => state.riders.riders);
  const hasMatchedRiders = useSelector((state) => state.riders.hasMatchedRiders);
  const hydratedRideDraftKeyRef = useRef(null);
  const skipDraftHydrationRef = useRef(false);
  const phoneSaveDebounceRef = useRef(null);
  const lastSubmittedPhoneRef = useRef('');

  const validationSchema = useMemo(
    () => stepperInfo[0].schema(showCovidField, showClinicSelect),
    [showCovidField, showClinicSelect],
  );

  const formik = useFormik({
    validationSchema,
    enableReinitialize: true,
    initialValues: stepperInfo[0].values,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {},
  });

  const { values, errors, isValid, setFieldValue, setValues, validateForm, touched, setFieldTouched } =
    formik;

  const riderInfo = values?.riderInfo || {};
  const isCovidCase = values?.covid19Relation === COVID_OPTIONS[1].value;
  const disableRiderInfo = isCovidCase;

  const onRiderSearch = useCallback(
    (value) => setFieldValue('formattedRiderName', value ?? ''),
    [setFieldValue],
  );

  const { notFoundContent, suggestions, handleSearchRider, handleSelectRider, riderIsPopulated, setRiderIsPopulated } =
    useRidersAutoComplete({
      handleSearch: onRiderSearch,
      value: values?.formattedRiderName,
      delay: 1000,
    });

  useEffect(() => {
    if (draft?.step1?.riderDraft?.id || draft?.step1?.riderInfo?.id) {
      setRiderIsPopulated(true);
    }
  }, [draft?.step1?.riderDraft?.id, draft?.step1?.riderInfo?.id, setRiderIsPopulated]);

  const syncedOrganizationIdRef = useRef(null);

  useEffect(() => {
    void validateForm();
  }, [showCovidField, showClinicSelect, validateForm]);

  useEffect(() => {
    if (!showClinicSelect || !values?.organizationId) return;
    if (syncedOrganizationIdRef.current === values.organizationId) return;
    syncedOrganizationIdRef.current = values.organizationId;
    onOrganizationChange?.(values.organizationId);
  }, [showClinicSelect, values?.organizationId, onOrganizationChange]);

  useEffect(() => {
    if (!draft?.step1) return;
    const hydratedStepOne = mergeDraftStepOneForForm(draft.step1);

    if (skipDraftHydrationRef.current) {
      skipDraftHydrationRef.current = false;
      hydratedRideDraftKeyRef.current = hydratedStepOne;
      return;
    }

    if (LodashHelper.isEqual(hydratedRideDraftKeyRef.current, hydratedStepOne)) return;

    hydratedRideDraftKeyRef.current = hydratedStepOne;

    void (async () => {
      await setValues(hydratedStepOne);
      await validateForm();
    })();
  }, [draft?.step1, setValues, validateForm]);

  useEffect(() => {
    skipDraftHydrationRef.current = true;
    onValuesChange?.(values);
  }, [values, onValuesChange]);

  useEffect(() => {
    let cancelled = false;
    void validationSchema.isValid(values).then((valid) => {
      if (!cancelled) onValidChange?.(Boolean(valid));
    });
    return () => {
      cancelled = true;
    };
  }, [values, validationSchema, onValidChange]);

  useEffect(() => {
    onMatchedRidersChange?.(hasMatchedRiders);
  }, [hasMatchedRiders, onMatchedRidersChange]);

  const uniqueRiderValues = useMemo(
    () => getUniqueUserFieldValues(values?.riderInfo),
    [values?.riderInfo],
  );

  const allowCheckIsUnique = useMemo(
    () =>
      !values?.riderInfo?.id &&
      !LodashHelper.isEqual(
        getUniqueUserFieldValues(stepperInfo[0].values.riderInfo),
        getUniqueUserFieldValues(values?.riderInfo),
      ),
    [values?.riderInfo?.id, values?.riderInfo],
  );

  useCheckRiderForMatching(uniqueRiderValues, allowCheckIsUnique);

  const handleCancelUseExistingRider = useCallback(() => {
    dispatch(clearRidersInfo());
  }, [dispatch]);

  const handleChangeRiderInfo = useCallback(
    async (rider) => {
      await setFieldValue('riderInfo', rider);
      await setFieldValue('formattedRiderName', RiderHelper.getRiderAutoCompleteName(rider));
      setRiderIsPopulated(true);
      dispatch(setRiderInfo(rider));
      dispatch(clearRidersInfo());
    },
    [dispatch, setFieldValue, setRiderIsPopulated],
  );

  const handleSubmitUseExistingRider = useCallback(
    async (formValues) => {
      const rider = riders.find((r) => r?.id === formValues?.riders[0]);
      await handleChangeRiderInfo(rider);
    },
    [riders, handleChangeRiderInfo],
  );

  const handleUseExactRider = useCallback(async () => {
    await handleChangeRiderInfo(riders[0]);
  }, [riders, handleChangeRiderInfo]);

  const setRiderInfoField = useCallback(
    async (name, val) => {
      if (riderInfo?.firstName && riderInfo?.lastName && name !== 'phoneNumber') {
        handleCancelUseExistingRider();
      }
      await setFieldValue(`riderInfo.${name}`, val);
      await setFieldTouched(`riderInfo.${name}`, true);
    },
    [handleCancelUseExistingRider, riderInfo, setFieldValue, setFieldTouched],
  );

  const handleCommunicationPreferences = useCallback(
    async (selected) => {
      if (selected?.includes(COMMUNICATION_TYPES.TEXT_SMS)) {
        await setFieldValue('riderInfo.canPhoneReceiveTexts', true);
      }
      if (selected?.includes(COMMUNICATION_TYPES.PHONE_CALL)) {
        await setFieldValue('riderInfo.canPhoneReceiveCalls', true);
      }
      await setFieldValue('riderInfo.communicationPreferences', selected);
      await setFieldTouched('riderInfo.communicationPreferences', true);
    },
    [setFieldValue, setFieldTouched],
  );

  const handleDobChange = useCallback(
    async (date) => {
      if (riderInfo?.firstName && riderInfo?.lastName) {
        handleCancelUseExistingRider();
      }
      if (!date) {
        await setFieldValue('riderInfo.dob', '');
      } else {
        await setFieldValue('riderInfo.dob', date.format(DOB_STORAGE_FORMAT));
      }
      await setFieldTouched('riderInfo.dob', true);
    },
    [handleCancelUseExistingRider, riderInfo, setFieldValue, setFieldTouched],
  );

  const dobValue = useMemo(() => parseDobValue(riderInfo?.dob), [riderInfo?.dob]);

  useEffect(() => {
    if (!handleSubmit) return undefined;

    const normalizedPhone = (riderInfo?.phoneNumber || '').replace(/\D/g, '');
    if (normalizedPhone.length !== 10) {
      if (phoneSaveDebounceRef.current) clearTimeout(phoneSaveDebounceRef.current);
      return undefined;
    }
    if (lastSubmittedPhoneRef.current === normalizedPhone) return undefined;

    if (phoneSaveDebounceRef.current) clearTimeout(phoneSaveDebounceRef.current);

    phoneSaveDebounceRef.current = setTimeout(() => {
      lastSubmittedPhoneRef.current = normalizedPhone;
      handleSubmit(values, '0');
    }, 600);

    return () => {
      if (phoneSaveDebounceRef.current) clearTimeout(phoneSaveDebounceRef.current);
    };
  }, [handleSubmit, riderInfo?.phoneNumber, values]);

  const languageOptions = languages.map((lang) => ({ value: lang.value, label: lang.title }));
  const commOptions = communicationsOptions.map((opt) => ({ value: opt.value, label: opt.title }));

  const onSelectRider = useCallback(
    (id, option) => {
      handleSelectRider({
        id,
        entity: option,
        name: 'formattedRiderName',
        setFieldValue,
      });
    },
    [handleSelectRider, setFieldValue],
  );

  const clinicOptions = useMemo(
    () =>
      (healthOrganizations || []).map((org) => ({
        value: org.id,
        label: org.name,
      })),
    [healthOrganizations],
  );

  const handleSelectOrganization = useCallback(
    async (organizationId) => {
      await setFieldValue('organizationId', organizationId, true);
      setFieldTouched('organizationId', true, false);
      onOrganizationChange?.(organizationId);
      await validateForm();
    },
    [setFieldValue, setFieldTouched, onOrganizationChange, validateForm],
  );

  return (
    <>
      {showClinicSelect && (
        <div className={styles.formRowFull}>
          <FieldLabel required>Select Facility for Ride</FieldLabel>
          <Select
            className={`${styles.inputFilled} ${styles.selectField}`}
            placeholder="Pick one"
            options={clinicOptions}
            value={values?.organizationId || undefined}
            onChange={handleSelectOrganization}
            showSearch
            optionFilterProp="label"
          />
          {touched?.organizationId && fieldError(errors, 'organizationId') && (
            <div className={styles.fieldError} role="alert">
              {fieldError(errors, 'organizationId')}
            </div>
          )}
        </div>
      )}

      {showCovidField && (
        <div className={styles.formRowFull}>
          <FieldLabel required>COVID-19</FieldLabel>
          <Select
            className={`${styles.inputFilled} ${styles.selectField}`}
            placeholder="Pick one"
            options={COVID_OPTIONS.map((o) => ({ value: o.value, label: o.title }))}
            value={values?.covid19Relation || undefined}
            onChange={(val) => setFieldValue('covid19Relation', val)}
          />
          {(values?.covid19Relation === COVID_OPTIONS[1].value ||
            fieldError(errors, 'covid19Relation')) && (
            <div className={styles.fieldError} role="alert">
              {fieldError(errors, 'covid19Relation') || COVID_ERROR_MESSAGE}
            </div>
          )}
        </div>
      )}

      <div className={styles.formRowFull}>
        <FieldLabel>Search for Rider</FieldLabel>
        <div className={`${styles.searchWithInfo} ${styles.inputFilled}`}>
          <AutoComplete
            className={styles.riderAutoComplete}
            options={suggestions}
            value={values?.formattedRiderName || ''}
            notFoundContent={notFoundContent}
            onSearch={(text) =>
              handleSearchRider({ name: 'formattedRiderName', value: text, setFieldValue })
            }
            onSelect={onSelectRider}
            placeholder="Search existing rider"
            disabled={disableRiderInfo}
          />
          <Tooltip
            title="If the name you entered into the search field is not found, begin entering the rider information into the fields below."
            color="#315e45"
            classNames={{ root: styles.searchInfoTooltip }}
          >
            <button type="button" className={styles.searchInfoBtn} aria-label="More information">
              <img src={infoIcon} alt="" aria-hidden />
            </button>
          </Tooltip>
        </div>
      </div>

      {hasMatchedRiders && riderInfo?.firstName && riderInfo?.lastName && riderInfo?.dob ? (
        <MatchedExactRiderSnackBar handleUseExactRider={handleUseExactRider} />
      ) : (
        hasMatchedRiders && (
          <MatchedRidersSnackBar
            riders={riders}
            handleSubmitUseExistingRider={handleSubmitUseExistingRider}
            handleCancelUseExistingRider={handleCancelUseExistingRider}
          />
        )
      )}

      <div className={styles.formRow}>
        <div>
          <FieldLabel required>First Name</FieldLabel>
          <Input
            className={styles.inputFilled}
            placeholder="First name"
            value={riderInfo.firstName || ''}
            disabled={disableRiderInfo || riderIsPopulated}
            onChange={(e) => setRiderInfoField('firstName', e.target.value)}
            onBlur={() => setFieldTouched('riderInfo.firstName', true)}
          />
          {touched?.riderInfo?.firstName && fieldError(errors, 'riderInfo.firstName') && (
            <div className={styles.fieldError} role="alert">
              {fieldError(errors, 'riderInfo.firstName')}
            </div>
          )}
        </div>
        <div>
          <FieldLabel required>Last Name</FieldLabel>
          <Input
            className={styles.inputFilled}
            placeholder="Last name"
            value={riderInfo.lastName || ''}
            disabled={disableRiderInfo || riderIsPopulated}
            onChange={(e) => setRiderInfoField('lastName', e.target.value)}
            onBlur={() => setFieldTouched('riderInfo.lastName', true)}
          />
          {touched?.riderInfo?.lastName && fieldError(errors, 'riderInfo.lastName') && (
            <div className={styles.fieldError} role="alert">
              {fieldError(errors, 'riderInfo.lastName')}
            </div>
          )}
        </div>
      </div>

      <div className={styles.formRow}>
        <div>
          <FieldLabel required>Date of Birth</FieldLabel>
          <DatePicker
            className={`${styles.inputFilled} ${styles.datePickerField}`}
            classNames={{ popup: { root: "quickScheduleNewDatePickerPopup" } }}
            format="MM/DD/YYYY"
            placeholder="MM/DD/YYYY"
            needConfirm
            value={dobValue}
            disabled={disableRiderInfo || riderIsPopulated}
            disabledDate={(current) => {
              if (!current) return false;
              const today = dayjs().startOf('day');
              const minDate = today.subtract(110, 'year');
              return current.valueOf() >= today.valueOf() || current.isBefore(minDate);
            }}
            onChange={handleDobChange}
            getPopupContainer={(trigger) =>
              trigger?.closest?.('.ant-modal-container, .ant-modal-content') || document.body
            }
            allowClear
          />
          {touched?.riderInfo?.dob && fieldError(errors, 'riderInfo.dob') && (
            <div className={styles.fieldError} role="alert">
              {fieldError(errors, 'riderInfo.dob')}
            </div>
          )}
        </div>
        <div>
          <FieldLabel>Language Preference</FieldLabel>
          <Select
            className={`${styles.inputFilled} ${styles.selectField}`}
            placeholder="Select language"
            options={languageOptions}
            value={riderInfo.preferredLanguage || undefined}
            allowClear
            disabled={disableRiderInfo}
            onChange={(val) => setRiderInfoField('preferredLanguage', val)}
          />
        </div>
      </div>

      <div className={styles.formRow}>
        <div>
          <FieldLabel required>Mobile Number</FieldLabel>
          <div className={styles.phoneInputWrap}>
            <span className={styles.phonePrefix}>+1</span>
            <Input
              className={styles.phoneInputField}
              placeholder="(000) 000-0000"
              value={formatPhoneDisplay(riderInfo.phoneNumber)}
              disabled={disableRiderInfo}
              onChange={(e) => {
                const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                setRiderInfoField('phoneNumber', digits);
              }}
              onBlur={() => setFieldTouched('riderInfo.phoneNumber', true)}
            />
          </div>
          {touched?.riderInfo?.phoneNumber && fieldError(errors, 'riderInfo.phoneNumber') && (
            <div className={styles.fieldError} role="alert">
              {fieldError(errors, 'riderInfo.phoneNumber')}
            </div>
          )}
        </div>
        <div>
          <FieldLabel>Communication Preferences</FieldLabel>
          <Select
            className={`${styles.inputFilled} ${styles.selectField} ${styles.selectFieldMultiple}`}
            mode="multiple"
            placeholder="Choose Preferences"
            options={commOptions}
            value={riderInfo.communicationPreferences || []}
            disabled={disableRiderInfo || riderIsPopulated}
            onChange={handleCommunicationPreferences}
            maxTagCount="responsive"
          />
        </div>
      </div>
    </>
  );
};

export default memo(RiderDetailsPanel);
